import {
    getFallbackAccountRoleForLineItem,
    LineItemFallbackAccountType,
} from "@common/helpers/accounting";
import { InvoiceLineItemKind } from "@common/types/invoice";
import { Account } from "@common/types/domains/accounting/account";
import { findEnabledAccountByRole } from "@common/helpers/accrualAccount";

interface AccountOverrideRef {
    accountId: string;
}

interface InvoiceLedgerLine {
    kind?: string | null;
    accountId?: string | null;
    accountsReceivableId?: string | null;
    accountOverrides?: AccountOverrideRef[];
    accountsReceivableOverrides?: AccountOverrideRef[];
}

export interface InvoiceLedgerAccounts {
    incomeAccountIds: string[];
    receivableAccountIds: string[];
    missingIncomeFallbacks: LineItemFallbackAccountType[];
    usesDefaultReceivableAccount: boolean;
}

function incomeFallbackForKind(
    kind: string | null | undefined,
): LineItemFallbackAccountType {
    switch (kind) {
        case InvoiceLineItemKind.TAX:
            return LineItemFallbackAccountType.salesTaxPayable;
        case InvoiceLineItemKind.DISCOUNT:
            return LineItemFallbackAccountType.discountsAndAllowances;
        case InvoiceLineItemKind.DEPOSIT:
            return LineItemFallbackAccountType.customerDeposits;
        default:
            return LineItemFallbackAccountType.invoiceRevenue;
    }
}

// The income / receivables columns summarise every coded account the
// invoice touches, not a single accounting basis. On a dual-ledger
// workspace a line can pin different accounts to the cash- and
// accrual-basis ledgers; we intentionally surface all of them here (the
// caller dedupes) rather than filtering by a "displayed" ledger, because
// the match drawer has no per-row basis to filter against.
function codedAccountIds(
    overrides: AccountOverrideRef[] | undefined,
    fallbackId: string | null | undefined,
): string[] {
    if (overrides && overrides.length > 0) {
        return overrides.map((entry) => entry.accountId);
    }
    return fallbackId ? [fallbackId] : [];
}

export interface BillLineExpenseAccounts {
    expenseAccountIds: string[];
    missingIncomeFallbacks: LineItemFallbackAccountType[];
}

export function billLineExpenseAccounts(line: {
    accountId?: string | null;
    accountOverrides?: AccountOverrideRef[];
}): BillLineExpenseAccounts {
    const expenseAccountIds = codedAccountIds(
        line.accountOverrides,
        line.accountId,
    );
    return {
        expenseAccountIds,
        missingIncomeFallbacks:
            expenseAccountIds.length === 0
                ? [LineItemFallbackAccountType.billExpense]
                : [],
    };
}

export function collectBillExpenseAccounts(
    lines: Array<{
        accountId?: string | null;
        accountOverrides?: AccountOverrideRef[];
    }>,
): BillLineExpenseAccounts {
    const expenseAccountIds: string[] = [];
    const missingIncomeFallbacks = new Set<LineItemFallbackAccountType>();

    for (const line of lines) {
        const lineAccounts = billLineExpenseAccounts(line);
        expenseAccountIds.push(...lineAccounts.expenseAccountIds);
        for (const fallback of lineAccounts.missingIncomeFallbacks) {
            missingIncomeFallbacks.add(fallback);
        }
    }

    return {
        expenseAccountIds: [...new Set(expenseAccountIds)],
        missingIncomeFallbacks: [...missingIncomeFallbacks],
    };
}

export function collectInvoiceLedgerAccounts(
    lines: InvoiceLedgerLine[],
): InvoiceLedgerAccounts {
    const incomeAccountIds: string[] = [];
    const receivableAccountIds: string[] = [];
    const missingIncomeFallbacks = new Set<LineItemFallbackAccountType>();
    let usesDefaultReceivableAccount = false;

    for (const line of lines) {
        const incomeIds = codedAccountIds(
            line.accountOverrides,
            line.accountId,
        );
        if (incomeIds.length > 0) {
            incomeAccountIds.push(...incomeIds);
        } else {
            missingIncomeFallbacks.add(incomeFallbackForKind(line.kind));
        }

        const receivableIds = codedAccountIds(
            line.accountsReceivableOverrides,
            line.accountsReceivableId,
        );
        if (receivableIds.length > 0) {
            receivableAccountIds.push(...receivableIds);
        } else {
            usesDefaultReceivableAccount = true;
        }
    }

    return {
        incomeAccountIds: [...new Set(incomeAccountIds)],
        receivableAccountIds: [...new Set(receivableAccountIds)],
        missingIncomeFallbacks: [...missingIncomeFallbacks],
        usesDefaultReceivableAccount,
    };
}

export function accountIdsForFallbacks(
    accounts: Account[],
    fallbacks: LineItemFallbackAccountType[],
): string[] {
    return fallbacks.flatMap((lineItemFallbackAccountType) => {
        const role = getFallbackAccountRoleForLineItem({
            lineItemFallbackAccountType,
        });
        const accountId = findEnabledAccountByRole(accounts, role)?.id;
        return accountId ? [accountId] : [];
    });
}

export function appendAccountIds(
    accountIds: string[],
    extraAccountIds: Array<string | null>,
): string[] {
    const seen = new Set(accountIds);
    const result = [...accountIds];
    for (const accountId of extraAccountIds) {
        if (!accountId || seen.has(accountId)) {
            continue;
        }
        seen.add(accountId);
        result.push(accountId);
    }
    return result;
}
